import { useState } from 'react'
import { useOutletContext, useParams } from 'react-router-dom'
import {
  searchUsers,
  useAddGroupMember,
  useAddMember,
  useCreateGroup,
  useDeleteGroup,
  useGroups,
  useMembers,
  useRemoveGroupMember,
  useRemoveMember,
  useSetMfaExemption,
} from '../api/rooms'
import { useAuth } from '../auth/AuthContext'
import type { RoomMemberResponse, RoomResponse, UserSummary } from '../api/types'
import { errorMessage, formatUtc } from '../lib/format'
import { Invitations } from '../components/Invitations'

/** Members and groups of the room. Owners manage; a participant with manage_permissions can look. */
export function People() {
  const room = useOutletContext<RoomResponse>()
  const { roomId = '' } = useParams()
  const isOwner = room.memberRole === 'Owner'
  const members = useMembers(roomId)
  const groups = useGroups(roomId)
  const { me } = useAuth()
  // Shown while owners may use it, and afterwards for as long as anyone is still exempt.
  const showSignIn =
    (me?.mfaExemptionsEnabled ?? false) ||
    (members.data?.some((m) => m.mfaExemptUtc !== null) ?? false)

  return (
    <div className="stack">
      <section className="panel">
        <h2 className="h2">Members</h2>
        {members.isError && <p className="error-text small">{errorMessage(members.error)}</p>}
        {members.data && (
          <table className="table">
            <thead>
              <tr>
                <th>Person</th>
                <th>Role</th>
                <th>Groups</th>
                <th>Access ends</th>
                {showSignIn && <th>Sign-in</th>}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {members.data.map((m) => (
                <tr key={m.userId}>
                  <td>
                    <div>{m.displayName ?? m.email ?? m.userId}</div>
                    {m.displayName && m.email && <div className="small muted">{m.email}</div>}
                  </td>
                  <td>
                    <span className="chip">{m.memberRole}</span>
                  </td>
                  <td className="small">{m.groups.join(', ')}</td>
                  <td className="small muted">
                    {isOwner ? (
                      <AccessEnd roomId={roomId} member={m} />
                    ) : m.accessEndsUtc ? (
                      formatUtc(m.accessEndsUtc)
                    ) : (
                      '—'
                    )}
                  </td>
                  {showSignIn && (
                    <td className="small">
                      <SignInRequirement
                        roomId={roomId}
                        member={m}
                        canChange={
                          isOwner &&
                          (me?.mfaExemptionsEnabled ?? false) &&
                          room.status !== 'Archived' &&
                          m.userId !== me?.userId
                        }
                      />
                    </td>
                  )}
                  <td className="td-actions">
                    {isOwner && <RemoveMemberButton roomId={roomId} userId={m.userId} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {isOwner && room.status !== 'Archived' && (
          <AddMember roomId={roomId} organizationId={room.organizationId} />
        )}
      </section>

      {isOwner && <Invitations room={room} roomId={roomId} />}

      <section className="panel">
        <h2 className="h2">Groups</h2>
        <p className="small muted">
          A group is a set of members that permissions can be granted to together, such as a
          bidder's legal team.
        </p>
        {groups.isError && <p className="error-text small">{errorMessage(groups.error)}</p>}
        {groups.data?.length === 0 && <p className="muted small">No groups yet.</p>}
        {groups.data?.map((g) => (
          <div key={g.id} className="group">
            <div className="row-between">
              <h3 className="h3 no-margin">{g.name}</h3>
              {isOwner && <DeleteGroupButton roomId={roomId} groupId={g.id} name={g.name} />}
            </div>
            <ul className="plain-list">
              {g.members.map((gm) => (
                <li key={gm.userId} className="row-between">
                  <span>
                    {gm.displayName ?? gm.email ?? gm.userId}
                    {gm.accessEndsUtc && (
                      <span className="small muted"> · until {formatUtc(gm.accessEndsUtc)}</span>
                    )}
                  </span>
                  {isOwner && (
                    <RemoveGroupMemberButton roomId={roomId} groupId={g.id} userId={gm.userId} />
                  )}
                </li>
              ))}
              {g.members.length === 0 && <li className="muted small">Nobody yet.</li>}
            </ul>
            {isOwner && members.data && (
              <AddToGroup
                roomId={roomId}
                groupId={g.id}
                candidates={members.data
                  .filter(
                    (m) =>
                      m.memberRole === 'Participant' &&
                      !g.members.some((gm) => gm.userId === m.userId),
                  )
                  .map((m) => ({ id: m.userId, label: m.displayName ?? m.email ?? m.userId }))}
              />
            )}
          </div>
        ))}
        {isOwner && room.status !== 'Archived' && <NewGroup roomId={roomId} />}
      </section>
    </div>
  )
}

function AddMember({ roomId, organizationId }: { roomId: string; organizationId: string }) {
  const add = useAddMember(roomId)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<UserSummary[] | null>(null)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [role, setRole] = useState<'Owner' | 'Participant'>('Participant')
  const [ends, setEnds] = useState('')

  const search = async () => {
    setSearchError(null)
    try {
      setResults(await searchUsers(organizationId, query))
    } catch (e) {
      setSearchError(errorMessage(e))
      setResults(null)
    }
  }

  return (
    <div className="stack-sm add-member">
      <h3 className="h3">Add a member</h3>
      <p className="small muted">
        Only people who have signed in to the Data Room at least once can be found here. To bring in
        someone new, send an invitation below.
      </p>
      <form
        className="inline-form"
        onSubmit={(e) => {
          e.preventDefault()
          void search()
        }}
      >
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Exact email, or the start of a name (3+ characters)"
          aria-label="Search people"
        />
        <button type="submit" className="btn" disabled={query.trim().length < 3}>
          Search
        </button>
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as 'Owner' | 'Participant')}
          aria-label="Role"
        >
          <option value="Participant">Participant</option>
          <option value="Owner">Owner</option>
        </select>
        <input
          type="datetime-local"
          value={ends}
          onChange={(e) => setEnds(e.target.value)}
          aria-label="Access ends"
        />
      </form>
      {searchError && <p className="error-text small">{searchError}</p>}
      {results?.length === 0 && <p className="muted small">Nobody matched.</p>}
      {results && results.length > 0 && (
        <ul className="plain-list">
          {results.map((u) => (
            <li key={u.id} className="row-between">
              <span>
                {u.displayName ?? u.email}{' '}
                {u.displayName && u.email && <span className="small muted">{u.email}</span>}
              </span>
              <button
                type="button"
                className="btn btn-sm"
                disabled={add.isPending}
                onClick={() =>
                  add.mutate(
                    {
                      userId: u.id,
                      memberRole: role,
                      accessEndsUtc: ends ? new Date(ends).toISOString() : null,
                    },
                    { onSuccess: () => setResults(null) },
                  )
                }
              >
                Add as {role}
              </button>
            </li>
          ))}
        </ul>
      )}
      {add.isError && <p className="error-text small">{errorMessage(add.error)}</p>}
    </div>
  )
}

/**
 * The member's access end, editable in place. Saving re-posts the membership with the
 * same role: the add-member endpoint is an upsert, so this is the one place expiry is
 * changed after the fact. An expired member is shown as such; the server already
 * treats them as no member at all.
 */
function AccessEnd({ roomId, member }: { roomId: string; member: RoomMemberResponse }) {
  const update = useAddMember(roomId)
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(member.accessEndsUtc ? member.accessEndsUtc.slice(0, 16) : '')
  const expired = member.accessEndsUtc !== null && new Date(member.accessEndsUtc) <= new Date()

  if (!editing) {
    return (
      <span>
        {member.accessEndsUtc ? formatUtc(member.accessEndsUtc) : 'No end'}
        {expired && <span className="chip chip-bad"> expired</span>}{' '}
        <button type="button" className="link-btn small" onClick={() => setEditing(true)}>
          change
        </button>
      </span>
    )
  }
  return (
    <form
      className="inline-form"
      onSubmit={(e) => {
        e.preventDefault()
        update.mutate(
          {
            userId: member.userId,
            memberRole: member.memberRole,
            accessEndsUtc: value ? new Date(value).toISOString() : null,
          },
          { onSuccess: () => setEditing(false) },
        )
      }}
    >
      <input
        type="datetime-local"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        aria-label="Access ends"
      />
      <button type="submit" className="btn btn-sm" disabled={update.isPending}>
        Save
      </button>
      <button type="button" className="btn btn-sm" onClick={() => setEditing(false)}>
        Cancel
      </button>
      {update.isError && <span className="error-text small">{errorMessage(update.error)}</span>}
    </form>
  )
}

/**
 * Whether this member needs two-step verification to open THIS room (ADR 0011). An owner
 * may let another member in with their password alone, a co-owner included (s9,
 * 2026-10-23), but never themselves; every other room still asks them. The server
 * enforces all of this; the control only offers what it would accept.
 */
export function SignInRequirement({
  roomId,
  member,
  canChange,
}: {
  roomId: string
  member: RoomMemberResponse
  canChange: boolean
}) {
  const set = useSetMfaExemption(roomId)
  const exempt = member.mfaExemptUtc !== null
  const name = member.displayName ?? member.email ?? 'this person'

  return (
    <span>
      {exempt ? (
        <>
          <span className="chip chip-warn">Password only</span>
          <div className="muted">
            since {formatUtc(member.mfaExemptUtc!)}
            {member.mfaExemptByName && <> · {member.mfaExemptByName}</>}
          </div>
        </>
      ) : (
        <span className="muted">Two-step</span>
      )}
      {canChange && (
        <div>
          <button
            type="button"
            className="link-btn small"
            disabled={set.isPending}
            onClick={() => {
              if (exempt) {
                set.mutate({ userId: member.userId, exempt: false })
              } else if (
                window.confirm(
                  `Let ${name} into this room with their password alone?

` +
                    'They will not be asked for an authenticator code to open this room. Every ' +
                    'other room still asks them, and you can require it again at any time.' +
                    (member.memberRole === 'Owner'
                      ? `

They are an owner: anyone with their password could manage this room, ` +
                        'its members and its documents.'
                      : ''),
                )
              ) {
                set.mutate({ userId: member.userId, exempt: true })
              }
            }}
          >
            {exempt ? 'Require two-step' : 'Allow password only'}
          </button>
        </div>
      )}
      {set.isError && <div className="error-text">{errorMessage(set.error)}</div>}
    </span>
  )
}

function RemoveMemberButton({ roomId, userId }: { roomId: string; userId: string }) {
  const remove = useRemoveMember(roomId)
  return (
    <>
      <button
        type="button"
        className="btn btn-sm btn-danger"
        disabled={remove.isPending}
        onClick={() => {
          if (
            window.confirm(
              'Remove this member from the room? Their explicit permissions go with them.',
            )
          )
            remove.mutate(userId)
        }}
      >
        Remove
      </button>
      {remove.isError && <span className="error-text small"> {errorMessage(remove.error)}</span>}
    </>
  )
}

function NewGroup({ roomId }: { roomId: string }) {
  const create = useCreateGroup(roomId)
  const [name, setName] = useState('')
  return (
    <form
      className="inline-form"
      onSubmit={(e) => {
        e.preventDefault()
        if (name.trim()) create.mutate(name.trim(), { onSuccess: () => setName('') })
      }}
    >
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="New group, e.g. Bidder A – Legal"
        aria-label="New group name"
      />
      <button type="submit" className="btn" disabled={create.isPending || !name.trim()}>
        Add group
      </button>
      {create.isError && <span className="error-text small">{errorMessage(create.error)}</span>}
    </form>
  )
}

function DeleteGroupButton({
  roomId,
  groupId,
  name,
}: {
  roomId: string
  groupId: string
  name: string
}) {
  const remove = useDeleteGroup(roomId)
  return (
    <button
      type="button"
      className="btn btn-sm btn-danger"
      disabled={remove.isPending}
      onClick={() => {
        if (window.confirm(`Delete the group "${name}"? Permissions granted through it end.`))
          remove.mutate(groupId)
      }}
    >
      Delete group
    </button>
  )
}

function AddToGroup({
  roomId,
  groupId,
  candidates,
}: {
  roomId: string
  groupId: string
  candidates: { id: string; label: string }[]
}) {
  const add = useAddGroupMember(roomId)
  const [userId, setUserId] = useState('')
  if (candidates.length === 0) return null
  return (
    <form
      className="inline-form"
      onSubmit={(e) => {
        e.preventDefault()
        if (userId) add.mutate({ groupId, userId }, { onSuccess: () => setUserId('') })
      }}
    >
      <select value={userId} onChange={(e) => setUserId(e.target.value)} aria-label="Member to add">
        <option value="">Add a participant…</option>
        {candidates.map((c) => (
          <option key={c.id} value={c.id}>
            {c.label}
          </option>
        ))}
      </select>
      <button type="submit" className="btn btn-sm" disabled={!userId || add.isPending}>
        Add
      </button>
      {add.isError && <span className="error-text small">{errorMessage(add.error)}</span>}
    </form>
  )
}

function RemoveGroupMemberButton({
  roomId,
  groupId,
  userId,
}: {
  roomId: string
  groupId: string
  userId: string
}) {
  const remove = useRemoveGroupMember(roomId)
  return (
    <button
      type="button"
      className="btn btn-sm"
      disabled={remove.isPending}
      onClick={() => remove.mutate({ groupId, userId })}
    >
      Remove
    </button>
  )
}
